import { useContext, useMemo } from 'react';
import { Badge } from 'reactstrap';
import { isEmpty } from 'lodash-es';
import cls from 'classnames';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { faPerson } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormContext } from '../../../common/forms/FormElements';
import { MapDataContext } from '../MapDataContext';
import { EmployeeMarkerIcon, getEmployeeStatusIndicators } from './EmployeeMarkerIcon';
import { getEmpCoords, getEmployeeId, getEmployeeName } from './common';

const filterByLookup = (employees, filterIds, available) => {
    if (isEmpty(filterIds)) {
        return employees;
    }

    return available
        .filter((x) => filterIds.includes(x.key)) // Get all "available" employees that are in the filters
        .map((x) => employees.find((employee) => employee.id === x.value)) // Map the employees to the filtered list
        .filter(Boolean); // Filter out any that are missing
};

export const EmployeeMarkers = ({ dispatchLocations }) => {
    const mapData = useContext(MapDataContext);
    const { values, setFieldValue } = useContext(FormContext);

    const {
        employeeSearch = '', attendanceFilters = [], longTermStatusFilters = [],
        jobCancellationFilters = [], assignmentStatusFilter = '', confirmationStatusFilter = '', employeeCertificationFilters = [],
        userGroupFilters = [], onCallStatusFilter = '', jobId, selectedEmployeeId,
    } = values;

    const filteredEmployees = useMemo(() => {
        const list = Array.isArray(mapData?.employees) ? mapData.employees : [];

        if (!mapData) {
            return list;
        }

        let filtered = list;
        filtered = filterByLookup(
            filtered,
            jobCancellationFilters,
            mapData.employeesByJobAssignmentCancellation,
        );
        filtered = filterByLookup(
            filtered,
            attendanceFilters,
            mapData.employeesByDailyAttendanceEvent,
        );
        filtered = filterByLookup(
            filtered,
            longTermStatusFilters,
            mapData.employeesByLongTermStatus,
        );
        filtered = filterByLookup(
            filtered,
            employeeCertificationFilters,
            mapData.employeesByEmployeeCertification,
        );
        filtered = filterByLookup(
            filtered,
            userGroupFilters,
            mapData.employeesByUserGroup,
        );

        if (assignmentStatusFilter) {
            const scheduledIds = new Set(
                (mapData.scheduled || []).map((e) => e?.value ?? e?.Value).filter(Boolean),
            );
            const notScheduledIds = new Set(
                (mapData.notScheduled || []).map((e) => e?.value ?? e?.Value).filter(Boolean),
            );

            filtered = filtered.filter((emp) => {
                const empId = emp?.employeeId ?? emp?.id ?? null;
                if (empId == null) return false;

                if (assignmentStatusFilter === 'scheduled') {
                    return scheduledIds.has(empId);
                }
                if (assignmentStatusFilter === 'notScheduled') {
                    return notScheduledIds.has(empId);
                }
                return true;
            });
        }

        if (confirmationStatusFilter) {
            const confirmedIds = new Set(
                (mapData.confirmed || []).map((e) => e?.value ?? e?.Value).filter(Boolean),
            );
            const notConfirmedIds = new Set(
                (mapData.notConfirmed || []).map((e) => e?.value ?? e?.Value).filter(Boolean),
            );

            filtered = filtered.filter((emp) => {
                const empId = emp?.employeeId ?? emp?.id ?? null;
                if (empId == null) return false;

                if (confirmationStatusFilter === 'confirmed') {
                    return confirmedIds.has(empId);
                }
                if (confirmationStatusFilter === 'notConfirmed') {
                    return notConfirmedIds.has(empId);
                }
                return true;
            });
        }

        if (onCallStatusFilter) {
            filtered = filtered.filter((emp) => {
                const isOnCall = emp?.isFlaggerAvailableForOnCall === true;
                if (onCallStatusFilter === 'onCall') return isOnCall;
                if (onCallStatusFilter === 'notOnCall') return !isOnCall;
                return true;
            });
        }

        if (employeeSearch.trim()) {
            const needle = employeeSearch.trim().toLowerCase();
            filtered = filtered.filter((emp) => {
                const firstName = emp?.firstName || '';
                const lastName = emp?.lastName || '';
                const city = emp?.homeAddress?.city || '';
                const addressParts = [
                    emp?.homeAddress?.address1,
                    emp?.homeAddress?.address2,
                    emp?.homeAddress?.city,
                    emp?.homeAddress?.state,
                    emp?.homeAddress?.zip,
                ].filter(Boolean);
                const address = addressParts.join(' ');
                const fullName = `${firstName} ${lastName}`.trim();

                return [
                    firstName,
                    lastName,
                    fullName,
                    city,
                    address,
                ]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(needle));
            });
        }

        // Mark employees as highlighted
        filtered = filtered.map((employee) => {
            if (!jobId || !Array.isArray(mapData?.crewStatuses)) {
                return employee;
            }

            return {
                ...employee,
                highlighted: mapData.crewStatuses.find((status) => status.jobId === jobId && status.employeeId === employee.id),
            };
        });

        return filtered;
    }, [
        mapData,
        attendanceFilters,
        longTermStatusFilters,
        jobCancellationFilters,
        employeeCertificationFilters,
        userGroupFilters,
        assignmentStatusFilter,
        confirmationStatusFilter,
        onCallStatusFilter,
        employeeSearch,
        jobId,
    ]);

    const groupedEmployees = useMemo(() => {
        const groups = {};

        filteredEmployees.forEach((emp) => {
            const coords = getEmpCoords(emp, dispatchLocations);

            if (coords) {
                const key = `${coords.lat},${coords.lng}`;

                if (!groups[key]) {
                    groups[key] = {
                        key,
                        position: coords,
                        employees: [],
                    };
                }

                groups[key].employees.push({
                    ...emp,
                    coordinates: coords,
                });
            }
        });

        return Object.values(groups);
    }, [filteredEmployees, dispatchLocations]);

    return groupedEmployees.map((group) => {
        const singleEmp = group.employees.length === 1 ? group.employees[0] : null;
        const singleEmpId = singleEmp ? getEmployeeId(singleEmp) : null;
        const indicators = singleEmpId != null
            ? getEmployeeStatusIndicators(singleEmpId, mapData)
            : [];

        const hasHighlighted = group.employees.some(
            (emp) => emp.highlighted,
        );

        const hasSelected = group.employees.some((emp) => emp.id === selectedEmployeeId);

        return (
            <AdvancedMarker
                key={group.key}
                position={group.position}
                title={
                    group.employees.length === 1
                        ? getEmployeeName(group.employees[0])
                        : `${group.employees.length} employees at this location`
                }
                onClick={() => {
                    setFieldValue('jobGroup', null);
                    setFieldValue('employeeGroup', group.employees);
                }}
                collisionBehavior="REQUIRED"
                className="marker-group"
            >
                {group.employees.length > 1 ? (
                    <div>
                        <FontAwesomeIcon
                            icon={faPerson}
                            size="2x"
                            className={cls('map-marker', hasHighlighted ? 'text-danger' : 'text-primary', hasSelected && 'map-marker-selected')}
                        />

                        <Badge
                            color="danger"
                            className="shadow border"
                            pill
                        >
                            {group.employees.length}
                        </Badge>
                    </div>
                ) : (
                    <EmployeeMarkerIcon
                        isHighlighted={singleEmp.highlighted}
                        indicators={indicators}
                        selected={hasSelected}
                    />
                )}
            </AdvancedMarker>
        );
    });
};